import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnnotationTool, useMediaPlayer } from '@/features/comment-on-file';
import type { VideoSyncFunctions } from '../../../../AssetCompare/CompareViewContext';
import type { AssetFile } from '../../../assetDetail.types';
import { VideoAnnotationBar, VideoControls } from '../../VideoControls';
import { useMediaBox } from '../hooks/useMediaBox';
import { useSpaceKeyState } from '../hooks/useSpaceKeyState';
import { ShapeOverlay } from './ShapeOverlay';
import { usePersistedEntries } from '../hooks/usePersistedEntries';

interface Props {
  file: AssetFile;
  annotationsEnabled?: boolean;
  showFullscreen?: boolean;
  syncPlay?: VideoSyncFunctions | null;
}

export function VideoOverlay({
  file,
  annotationsEnabled = true,
  showFullscreen = true,
  syncPlay,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { box, onLoad } = useMediaBox(videoRef);
  const [currentTime, setCurrentTime] = useState(0);
  const videoMode = useMemo(() => ({ kind: 'video' as const, currentTime }), [currentTime]);
  const persisted = usePersistedEntries(videoMode);
  const { registerMediaElement } = useMediaPlayer();
  const { tool } = useAnnotationTool();
  const isHoveredRef = useRef(false);

  useEffect(() => {
    registerMediaElement(videoRef.current);
    return () => registerMediaElement(null);
  }, [registerMediaElement]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !syncPlay) return;

    const unregister = syncPlay.registerVideo(el);

    const onPlay = () => {
      if (syncPlay.isSourceAction(el)) syncPlay.notifyPlay(el);
    };
    const onPause = () => {
      if (syncPlay.isSourceAction(el)) syncPlay.notifyPause(el);
    };
    const onSeeking = () => {
      if (syncPlay.isSourceAction(el)) syncPlay.notifySeek(el, el.currentTime);
    };

    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('seeking', onSeeking);

    return () => {
      unregister();
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('seeking', onSeeking);
    };
  }, [syncPlay]);

  useEffect(() => {
    if (!annotationsEnabled) return;
    const el = videoRef.current;
    if (tool !== null && el && !el.paused) el.pause();
  }, [tool, annotationsEnabled]);

  useSpaceKeyState({
    onPress: (e) => {
      const el = videoRef.current;
      if (!isHoveredRef.current || !el) return;
      if (e.repeat) return;

      e.preventDefault();
      if (el.paused) void el.play();
      else el.pause();
    },
  });

  return (
    <div
      className="group relative flex min-h-0 min-w-0 flex-1 flex-col"
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
    >
      <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-tl-lg rounded-tr-lg bg-muted dark:bg-background">
        <video
          ref={videoRef}
          src={file.downloadUrl ?? undefined}
          className="max-h-full max-w-full"
          onLoadedMetadata={onLoad}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setCurrentTime((prev) => (Math.abs(prev - t) < 0.05 ? prev : t));
          }}
        >
          <track kind="captions" />
        </video>
        {annotationsEnabled && <ShapeOverlay box={box} persisted={persisted} />}
        <VideoControls annotationsEnabled={annotationsEnabled} showFullscreen={showFullscreen} />
      </div>
      {annotationsEnabled && <VideoAnnotationBar />}
    </div>
  );
}
